/**
 * Home health demo — the one door every clock time on screen goes through.
 *
 * Two rules, and the demo has no other way of phrasing an hour.
 *
 * **The product's setting.** `useZonedDateFormat()` carries the military-time
 * switch from the user menu (`medarrive-time-format-preference`, default
 * twelve-hour), and every label here is built by the product's own helpers in
 * `@caredna-marlin/utils/dates`. So the demo reads `9:45AM` for most people and
 * `0945` for somebody with the military clock on, exactly as the rest of the web
 * app does — including `timeRangeLabel`'s colon-less `0945-1045`, which is the
 * product's own output and not something the demo gets to improve on.
 *
 * **The branch's zone, always.** Only `timeLabel`, `timeLabelWithZone` and
 * `timeRangeLabel` take a timezone override; `timeMarkLabel`, `dateTimeLabel`
 * and `fmt.datef` are bound to the browser's zone by the provider and would
 * render a Los Angeles branch in whatever zone the presenter's laptop is in. So
 * nothing here goes through a bound formatter method: the preference is read off
 * the context and the **bare exported helpers** are called with the branch zone
 * passed explicitly.
 *
 * A wall-clock `"HH:mm"` is rendered on an arbitrary UTC day rather than on the
 * branch's calendar: the label depends only on the hour and the minute, and
 * pinning it to UTC keeps a date out of an API that was never given one and
 * keeps DST out of the answer.
 */
import { TimeFormatPreference, timeLabel, timeMarkLabel, timeRangeLabel } from "@caredna-marlin/utils/dates";
import { useMemo } from "react";
import { loadTimeFormatPreference, useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { formatInBranchZone } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";

/**
 * Five minutes: a quarter is too coarse for a window an hour long, and a minute
 * is the precision that made this look computed in the first place. Whole
 * minutes divide every real zone offset, so rounding on the instant lands on a
 * five-minute wall-clock mark wherever the branch is.
 */
const ARRIVAL_STEP_MS = 5 * 60_000;
const floorToArrivalStep = (at: string | Date) => new Date(Math.floor(new Date(at).getTime() / ARRIVAL_STEP_MS) * ARRIVAL_STEP_MS);
const ceilToArrivalStep = (at: string | Date) => new Date(Math.ceil(new Date(at).getTime() / ARRIVAL_STEP_MS) * ARRIVAL_STEP_MS);
import { useDemoSelector } from "../store/useDemoStore";
import { isInstantTime, type TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";

export interface BranchClock {
  preference: TimeFormatPreference;
  timeZone: string;
  /** A time of day from an instant, in the branch's zone. `9:45AM` / `0945`. */
  time(at: string | Date): string;
  /** A time of day from a branch-zone `"HH:mm"` wall clock. `9:45AM` / `0945`. */
  wall(hhmm: string): string;
  /**
   * An arrival window from two instants, **rounded to five minutes and
   * outward**. `9:45-10:45AM` / `0945-1045`.
   *
   * The window is the visit's span with a quarter-hour of grace in front, so it
   * inherits whatever minute the day's arithmetic landed on — *10:42–11:42*,
   * *12:36–1:36*. Nobody tells a patient that. Pete, 2026-09-21: *"round the
   * display of these arrival windows."*
   *
   * Outward, never inward: the start rounds down and the end rounds up, so the
   * window shown always **contains** the window promised. Rounding to the
   * nearest would have narrowed it by up to two minutes at each end, and an
   * arrival window is a promise — it may be generous, it may not be short.
   *
   * Display only. The instants the engine compares, the confirmation round's
   * tolerance and the day axis's geometry all still use the real values.
   */
  range(startIso: string | Date, endIso: string | Date): string;
  /** An hour mark for a time axis, from a branch-zone `"HH:mm"`. `9am` / `0900`. */
  mark(hhmm: string): string;
  /**
   * A day's hours where a cell has no room for a meridiem: `8:00–5:00` / `0800–1700`.
   * `getTimeFormat({ ampm: false })` is the product's own meridiem-less shape.
   */
  hours(startHhmm: string, endHhmm: string): string;
  /** A day and a time from one instant. `Thu Sep 10 · 9:45AM`. */
  dateTime(at: string | Date, datePattern?: string): string;
  /**
   * The presenter log's stamp, to the second: `9:45:07AM` / `0945:07`. Written
   * out as two explicit patterns rather than through `datef`, whose
   * twenty-four-hour rewrite matches `h:mm` inside `h:mm:ssa` and would turn the
   * stamp into `HHmm:ssa`.
   */
  stamp(at: string | Date): string;
  /** A sentence the engine composed with time tokens in it. */
  sentence(sentence: TimeSentence): string;
}

/** Any day at all: a wall clock's label depends on the hour and the minute alone. */
const WALL_DAY = "2000-01-01";

function wallInstant(hhmm: string): Date {
  const [h, m] = hhmm.split(":").map(Number);
  return new Date(`${WALL_DAY}T${String(h || 0).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}:00.000Z`);
}

export function branchClock(timeZone: string, preference: TimeFormatPreference): BranchClock {
  const time = (at: string | Date) => timeLabel(at, { ampm: true, timezone: timeZone, timeFormat: preference });
  const wall = (hhmm: string) => timeLabel(wallInstant(hhmm), { ampm: true, timezone: "UTC", timeFormat: preference });
  const bare = (hhmm: string) => timeLabel(wallInstant(hhmm), { ampm: false, timezone: "UTC", timeFormat: preference });
  return {
    preference,
    timeZone,
    time,
    wall,
    range: (startIso, endIso) =>
      timeRangeLabel(floorToArrivalStep(startIso), ceilToArrivalStep(endIso), { showDate: false, showTimeZone: false, timezone: timeZone, timeFormat: preference }),
    mark: (hhmm) => timeMarkLabel(wallInstant(hhmm), { timezone: "UTC", timeFormat: preference }),
    hours: (startHhmm, endHhmm) => `${bare(startHhmm)}–${bare(endHhmm)}`,
    dateTime: (at, datePattern = "EEE MMM d") => `${formatInBranchZone(new Date(at), timeZone, datePattern)} · ${time(at)}`,
    stamp: (at) => formatInBranchZone(new Date(at), timeZone, preference === TimeFormatPreference.h24 ? "HHmm:ss" : "h:mm:ssa"),
    sentence: (sentence) => (typeof sentence === "string" ? sentence : sentence.map((part) => (typeof part === "string" ? part : isInstantTime(part) ? time(part.timeIso) : wall(part.hhmm))).join("")),
  };
}

/** The clock inside a React tree: the branch's zone and the signed-in user's own setting. */
export function useBranchClock(timeZone: string): BranchClock {
  const fmt = useZonedDateFormat();
  return useMemo(() => branchClock(timeZone, fmt.timeFormatPreference), [timeZone, fmt.timeFormatPreference]);
}

/** The same clock, with the branch's zone taken off the demo store — for a component that is not already holding it. */
export function useDemoBranchClock(): BranchClock {
  return useBranchClock(useDemoSelector((s) => s.config.branch.timeZone));
}

/**
 * The clock outside a React tree — the presenter log, which is written by a store
 * action rather than rendered by a component. The preference is read straight off
 * the same localStorage key the provider reads, so a logged line is phrased the
 * way the screen beside it is. A line already written is a stored string, so
 * flipping the switch re-phrases the screen and leaves the log's history alone.
 */
export function currentBranchClock(timeZone: string): BranchClock {
  return branchClock(timeZone, loadTimeFormatPreference());
}
